import {
  AIMode,
  AIThoughtInterface,
  AIStatus,
  ChannelAvailabilityInterface,
  ConversationChannel,
  EventType,
  GetMultiConversationDetailsV2ResponseDetailedConversation,
  MediaInterface,
  MessageType,
  Participant,
  ParticipantType,
  PlatformLocation,
  SendStatusInterface,
  UIComponentInterface,
} from '@vendasta/conversation';
import {
  AIMode as AIModeProto,
  AIStatus as AIStatusProto,
  ConversationChannel as ConversationChannelProto,
  EventType as EventTypeProto,
  MessageType as MessageTypeProto,
  ParticipantType as ParticipantTypeProto,
  PlatformLocation as PlatformLocationProto,
} from '@vendasta/protos/conversation/v1/enums';
import { BadgeColor } from '@vendasta/galaxy/badge/src/badge.component';
import { Timestamp } from '@angular/fire/firestore';
import { Event } from '@vendasta/conversation';
export { SubjectParticipant } from '@vendasta/conversation';

// Compat types: accept both old numeric enums
// and new string union proto types.
export type ConversationChannelCompat =
  | ConversationChannel
  | ConversationChannelProto;
export type MessageTypeCompat =
  | MessageType
  | MessageTypeProto;
export type ParticipantTypeCompat =
  | ParticipantType
  | ParticipantTypeProto;
export type EventTypeCompat =
  | EventType
  | EventTypeProto;
export type AIStatusCompat =
  | AIStatus
  | AIStatusProto;
export type AIModeCompat =
  | AIMode
  | AIModeProto;
export type PlatformLocationCompat =
  | PlatformLocation
  | PlatformLocationProto;

/**
 * Slim Firestore trigger doc backed by the ConversationLookup VStore kind.
 * Two PubSub hops downstream of the Conversation VStore write, so by the time
 * a doc appears here the RPC read model is already consistent. Filter fields
 * are intentionally omitted — filtering is delegated to the RPC.
 */
export interface FirestoreConversationLookup {
  id?: string;
  conversationId?: string;
  scopeId?: string;
  latestRelevantActivityTime?: Timestamp;
}

export interface FirestoreConversation {
  id?: string;
  conversationId?: string;
  externalConversationId?: string;
  previewContent?: string;
  channel?: ConversationChannelCompat;
  /**
   * @deprecated This property exists for historical compatibility.  Use subjectParticipantsKey instead.
   */
  accountGroupId?: string;
  /**
   * @deprecated This property exists for historical compatibility.  Use subjectParticipantsKey instead.
   */
  partnerId?: string;
  /**
   * @deprecated This property exists for historical compatibility. Use subjectParticipantsKey instead.
   */
  vendorPartnerId?: string;
  latestMsgSentTime?: Timestamp;
  created?: Timestamp;
  deleted?: Timestamp;
  updated?: Timestamp;
  /**
   * @deprecated This property exists for historical compatibility
   */
  lastSeen?: Timestamp | null;
  lastSeenByParticipants?: Array<FirestoreLastSeenByParticipant>;
  conversationViewIds?: Array<string>;
  /**
   * Array of computed pair keys for all unique 2-combinations of subject participants.
   * Each key is formatted as "type_snake:id|type_snake:id" (lexically ordered).
   */
  subjectParticipantPairKeyIndex: string[];
}

export interface ConversationMessage {
  id?: string;
  conversationId?: string;
  messageId?: string;
  externalMessageId?: string;
  sender?: Participant;
  type: MessageTypeCompat;
  channel: ConversationChannelCompat;
  body?: string;
  originalContent?: string;
  media?: Media[];
  sendStatus?: SendStatusInterface;
  metadata?: KeyValuePair[];
  created?: Date;
  deleted?: Date;
  updated?: Date;
  UIComponents?: UIComponentInterface[];
  aiThoughts?: AIThoughtInterface[];
  aiStatus?: AIStatusCompat;
  emailSubject?: string;
  emailContentHidden?: boolean;
  hidden?: boolean;
}

export interface KeyValuePair {
  key: string;
  value: string;
}

export interface FirestoreEvent {
  conversationId: string;
  eventId: string;
  happenedAt: Date;
  labelKey: string;
  initiator?: string;
  /**
   * @deprecated This property exists for historical compatibility. Use {@link Event#message} type instead.
   */
  message?: string;
  created: Date;
  updated: Date;
  deleted: Date;
}

export interface ConversationUnseen {
  conversationId?: string;
  read: boolean;
}

export interface FirestoreLastSeenByParticipant {
  participantId?: string;
  lastSeenTime?: Timestamp;
}

export interface ConversationBadge {
  participantType: string;
  badgeColor: BadgeColor;
}

export type ConversationDetail = GetMultiConversationDetailsV2ResponseDetailedConversation;

export interface ConversationDetailCache {
  [conversationId: string]: ConversationDetail;
}

export interface SendMessage {
  participants?: Array<Participant>;
  location: PlatformLocationCompat;
  channel: ConversationChannelCompat;
}

export interface ConversationTitleInfo {
  title: string;
  subtitle?: string;
  secondarySubtitle?: string;
}

export interface ConversationRecipientInfo {
  id: string;
  type: ParticipantTypeCompat;
}

export interface KabobDynamicButton {
  title: string;
  action: () => void;
  posthogLabel: string;
}

export interface TermsOfServiceResult {
  termsOfServiceTextMessage: string | null;
  termsOfServiceAccepted: boolean;
}

export interface Media {
  mediaUrl: string;
  mediaContentType: string;
  mediaFileName: string;
  mediaLocationPath: string;
  fileSize?: number;
}

export function convertMediaJSONIntoMedia(media: string): Media | null | 'not-json' {
  if (!media) {
    return null;
  }
  try {
    const data = JSON.parse(media);
    return {
      mediaUrl: data.MediaUrl,
      mediaContentType: data.MediaContentType,
      mediaFileName: data.MediaFileName,
      mediaLocationPath: data.MediaLocationPath,
      fileSize: data.FileSize,
    };
  } catch {
    return 'not-json';
  }
}

export interface MessageInfo {
  text?: string;
  template?: {
    id: string;
    bodyParameters: {
      field: number;
      text: string;
    }[];
  };
  channel: ConversationChannelCompat;
  attachments: MediaInterface[];
  aiMode?: AIModeCompat;
}

export interface ConversationAvailableChannels {
  availableChannels: ConversationChannelCompat[];
  channelAvailabilities: ChannelAvailabilityInterface[];
  preferredChannel: ConversationChannelCompat;
}

export interface BookingAvailabilityOption {
  displayText: string;
  scheduleId: string;
}

export interface EventInfo {
  id: string;
  icon?: string;
  svgIcon?: string;
  title: string;
  subtitle?: string;
  content: string;
  sentBy: string;
  sentVia: string;
  EventType: EventTypeCompat;
  IsOutBound: boolean;
  originEventUrl?: string;
  emailId?: string;
  channel?: string;
  callFromNumber?: string;
  callToNumber?: string;
  callDuration?: number;
  callRecordingUrl?: string;
  isAIVoiceCall?: boolean;
  transcriptJSON?: string;
  callRecordId?: string;
  assistantId?: string;
  assistantName?: string;
  callToAction?: () => void;
}

export interface ConversationEvent {
  created: Date;
  type: EventTypeCompat;
  channel: ConversationChannelCompat;
  event: Event;
  eventInfo: EventInfo;
  isFirstItem: boolean;
}

export interface InboxItem {
  id: string;
  sender?: Participant;
  created?: Date;
  type: EventTypeCompat | MessageTypeCompat;
  channel: ConversationChannelCompat;
  item: ConversationEvent | ConversationMessage;
  sentVia: string;
  itemFrom: string;
}

export interface EscalateToSupportPayload {
  conversationSummary: string;
}

/**
 * A message UIComponent carrying an interactive chart (DALE-1453). The deprecated
 * `@vendasta/conversation` SDK doesn't know UI_COMPONENT_TYPE_CHART — its enum
 * resolver turns the unknown string into `undefined` — but `UIComponent.fromProto`
 * Object.assigns the raw proto first, so the `chart` payload survives at runtime.
 * Detect charts by payload presence, never by `type`.
 */
export type ChartUIComponentInterface = UIComponentInterface & {
  chart?: {
    /** Opaque, producer-versioned ApexCharts options object (JSON), from ai-assistants. */
    apexConfigJson?: string;
  };
};
